import React, { useEffect, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import ReactGA from 'react-ga4';

interface MonitoringProviderProps {
  children: ReactNode;
}

/**
 * 🏛️ INSTITUTIONAL OBSERVABILITY SUITE
 * Single Source of Truth for Sentry, PostHog, and GA4.
 */
export const MonitoringProvider: React.FC<MonitoringProviderProps> = ({ children }) => {
  useEffect(() => {
    // 1. Environment Awareness
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0] || 'local';

    // 2. Initialize Sentry (Zero-Crash Hardening)
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0, 
        tracePropagationTargets: ["localhost", /^https:\/\/api\.laqy\.net/],
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        environment: subdomain,
        initialScope: {
          tags: { subdomain },
        },
      });
    }

    // 3. Initialize PostHog (Intent Capture)
    if (import.meta.env.VITE_POSTHOG_KEY) {
      posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
        autocapture: true,
        session_recording: {
          maskAllInputs: true,
        },
        capture_pageview: true,
        persistence: 'localStorage',
        bootstrap: {
          distinctID: 'anonymous',
        },
        property_blacklist: ['$ip', '$performance'], // Zero-Leakage of sensitive data
        loaded: (ph) => {
          ph.register({ subdomain });
        },
      });
    }

    // 4. Initialize GA4 (Cross-Domain Orchestration)
    if (import.meta.env.VITE_GA4_MEASUREMENT_ID) {
      ReactGA.initialize(import.meta.env.VITE_GA4_MEASUREMENT_ID, {
        gaOptions: {
          cookieDomain: 'laqy.net',
        },
      });
      ReactGA.gtag('config', import.meta.env.VITE_GA4_MEASUREMENT_ID, {
        subdomain: subdomain,
      });
    }
  }, []);

  return (
    <Sentry.ErrorBoundary
      fallback={(errorData: any) => {
        const { error, resetError } = errorData;
        return (
          <div className="min-h-screen bg-[#001f3f] flex items-center justify-center p-6 text-white text-center">
            <div className="max-w-md">
              <h1 className="text-4xl font-black mb-4 uppercase tracking-tighter">Critical Node Failure</h1>
              <p className="opacity-60 mb-8 font-medium">The Trinity Sync encountered a localized exception in the global node network. Our engineers have been notified.</p>
              <button 
                onClick={resetError}
                className="px-10 py-4 bg-white text-[#001f3f] font-black rounded-full hover:scale-105 transition-all shadow-xl active:scale-95"
              >
                REBOOT_SYSTEM_INSTANCE
              </button>
              {import.meta.env.DEV && (
                 <pre className="mt-8 p-4 bg-black/40 rounded text-left text-xs overflow-auto border border-white/10 font-mono">
                   {error?.message || 'Unknown Trinity Error'}
                 </pre>
              )}
            </div>
          </div>
        );
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default MonitoringProvider;
